/**
 * Centralized route configuration
 * 
 * All routes should be defined here and imported where needed.
 * This prevents hardcoded routes scattered across the codebase.
 * 
 * Rules of Engagement:
 * - Never hardcode /inventory/... or other paths in feature components
 * - Always use buildListRoute(doctype), buildDetailRoute(doctype, name), buildNewRoute(doctype)
 * - If you need a new route, add it here first, then add to doctypeToRouteKey in docRoutes.ts
 * 
 * Usage:
 *   import { routes } from "@/lib/config/routes";
 *   navigate(routes.item.detail(itemName));
 */
import { GRIDA_ORIGIN, STOREFRONT_ORIGIN } from "./preview-origins";

export const routes = {
  customer: {
    list: "/customers",
    new: "/customers/new",
    detail: (name: string) => `/customers/${encodeURIComponent(name)}`,
  },
  item: {
    list: "/item",
    new: "/inventory/item/new",
    detail: (name: string) => `/inventory/item/${encodeURIComponent(name)}`,
  },
  salesOrder: {
    list: "/orders",
    new: "/orders/new",
    detail: (name: string) => `/orders/${name}`,
  },
  stockEntry: {
    list: "/stock/stock-entry",
    new: "/stock/stock-entry/new",
    detail: (name: string) => `/stock/stock-entry/${name}`,
  },
  stockReconciliation: {
    list: "/stock/stock-reconciliation",
    new: "/stock/stock-reconciliation/new",
    detail: (name: string) => `/stock/stock-reconciliation/${name}`,
  },
  itemPrice: {
    list: "/inventory/item-price",
    new: "/inventory/item-price/new",
    detail: (name: string) => `/inventory/item-price/${name}`,
  },
  pricingRule: {
    list: "/inventory/pricing-rule",
    new: "/inventory/pricing-rule/new",
    detail: (name: string) => `/inventory/pricing-rule/${name}`,
  },
  shippingRateRule: {
    list: "/inventory/shipping-rate-rule",
    new: "/inventory/shipping-rate-rule/new",
    detail: (name: string) => `/inventory/shipping-rate-rule/${name}`,
  },
  salesInvoice: {
    list: "/selling/sales-invoice",
    new: "/selling/sales-invoice/new",
    detail: (name: string) => `/selling/sales-invoice/${name}`,
  },
  paymentEntry: {
    list: "/selling/payment-entry",
    new: "/selling/payment-entry/new",
    detail: (name: string) => `/selling/payment-entry/${name}`,
  },
  storefrontTheme: {
    list: "/storefront/theme",
    new: "/storefront/theme/new",
    detail: (name: string) => `/storefront/theme/${encodeURIComponent(name)}`,
  },
  storefrontPageConfig: {
    list: "/storefront/page-config",
    new: "/storefront/page-config/new",
    detail: (name: string) => `/storefront/page-config/${encodeURIComponent(name)}`,
  },
  customizationTemplate: {
    list: "/inventory/customization-templates",
    new: "/inventory/customization-templates/new",
    detail: (name: string) => `/inventory/customization-templates/${encodeURIComponent(name)}`,
  },
  grida: {
    /**
     * Get Grida editor URL for a page config.
     * This will call the backend to get a handoff token, then return the consume URL.
     * For now, returns a placeholder that will be replaced by the hook.
     */
    editor: (pageConfigName: string): string => {
      // This is a placeholder - actual URL will be generated by useGridaEditorHandoff hook
      // which calls the backend to get the token
      return `${GRIDA_ORIGIN}/editor/${encodeURIComponent(pageConfigName)}`;
    },
    /**
     * Get preview URL for a page config (for Grida to open preview).
     * This calls the backend to get the preview URL with proper tenant context.
     */
    preview: (pageConfigName: string, tenant?: string): string => {
      // This is a placeholder - actual URL will be generated by backend API
      // Grida should call get_grida_preview_url endpoint
      let url = `${STOREFRONT_ORIGIN}/preview/page?pageConfig=${encodeURIComponent(pageConfigName)}`;
      if (tenant) {
        url += `&tenant=${encodeURIComponent(tenant)}`;
      }
      return url;
    },
  },
} as const;

// Type-safe route helpers
export type RouteConfig = typeof routes;

